import { CommonModule } from '@angular/common';
import { Component, Inject, inject, Injector, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { ContactInfoCardComponent, GalaxyContactInfoCardModule } from '@vendasta/galaxy/contact-info-card';
import { GalaxyFilterChipInjectionToken } from '@vendasta/galaxy/filter/chips';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Row } from '@vendasta/galaxy/table';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { formatAsReadonlyPhone, parsePhoneNumber } from '../../model-driven-form';
import {
  CrmInjectionToken,
  CrmDependencies,
  CardData,
  COMPANY_PROFILE_CARD_DATA_TOKEN,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
  tableFiltersInjectionTokenGenerator,
} from '../../../tokens-and-interfaces';
import { PAGE_ROUTES, SNACKBAR_DURATION } from '../../../constants';
import { TranslationModule } from '../../../i18n';
import { CrmCreateModalContactComponent } from '../../create-crm-object-modal';
import { CrmAssociationService, Association } from '../../../shared-services';
import {
  CrmFieldService,
  CrmObjectService,
  ContactTableCustomizationService,
  CompanyTableCustomizationService,
  DefaultAssociationActionsService,
  StandardIds,
  combineAssociationsWithChanges,
  StandardExternalIds,
} from '../../../shared-services';
import { AssociationCardComponent, CardAction, TagsComponent } from '../association-card';
import { AssociationsDialogData } from '../select-associations-modal/select-associations-modal.component';
import {
  SelectObjectTypeModalComponent,
  ListObjectTypeDialogData,
} from '../../select-object-type-modal/select-object-type-modal.component';
import { CommonAssociationCardContentComponent } from '../association-card';
import {
  RemoveAssociationDialogComponent,
  RemoveAssociationData,
} from '../remove-association-dialog/remove-association-dialog.component';
import { AddCampaignDialogComponent } from '../contact-campaign-association-card/add-campaign-dialog/add-campaign-dialog.component';

interface CompanyToContactAssociationsCardData extends CardData {
  associationId: string;
  companyName: string;
}

@Component({
  selector: 'crm-company-to-contact-associations-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslationModule,
    MatExpansionModule,
    ContactInfoCardComponent,
    AssociationCardComponent,
    CommonAssociationCardContentComponent,
    TagsComponent,
    GalaxyAlertModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    RouterLink,
    MatMenuModule,
    GalaxyContactInfoCardModule,
    MatDialogModule,
    MatCardModule,
  ],
  providers: [
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
    },
  ],
  templateUrl: './company-to-contact-associations-card.component.html',
  styleUrls: [
    './company-to-contact-associations-card.component.scss',
    '../../../shared-styles/association-panel-shared.scss',
  ],
})
export class CompanyToContactAssociationsCardComponent implements OnInit {
  protected readonly pageSize = 5;
  data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  loadAssociations$$ = new BehaviorSubject<Association[]>([]);
  preSelectedRows$$ = new BehaviorSubject<Association[]>([]);
  fullName$: Observable<string>;
  modalData$: Observable<AssociationsDialogData>;
  canOpenModal = false;
  contacts$: Observable<CompanyToContactAssociationsCardData[]>;
  removedAssociationIds$$ = new BehaviorSubject<string[]>([]);
  openCreateContactModal$$ = new ReplaySubject<boolean>(1);
  openCreateContactModal$ = this.openCreateContactModal$$.asObservable();
  existingAssociations$: Observable<Association[]>;
  newContact$$ = new BehaviorSubject<string>('');
  changedAssociations$$ = new BehaviorSubject<Association[]>([]);
  readonly visibleContactsCount$$ = new BehaviorSubject<number>(this.pageSize);
  readonly hasMore$: Observable<boolean>;

  private readonly injector = inject(Injector);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly objectService: CrmObjectService,
    private readonly fieldService: CrmFieldService,
    private readonly associationService: CrmAssociationService,
    private readonly dialog: MatDialog,
    private translateService: TranslateService,
    private readonly contactCustomizationService: ContactTableCustomizationService,
    private readonly companyCustomizationService: CompanyTableCustomizationService,
    private crmAssociationService: CrmAssociationService,
    private readonly snackService: SnackbarService,
    private readonly defaultAssociationActionsService: DefaultAssociationActionsService,
  ) {
    this.associationService.setCrmObjectIdToAssociate(this.data?.company_id || '');
    this.fullName$ = objectService.getMultiObject('Company', [this.data.company_id || '']).pipe(
      map((resp) => {
        const object = resp?.crmObjects?.[0];
        if (resp?.crmObjects?.length === 0 || !object) {
          return '';
        }
        return this.companyCustomizationService.getRowFullName(object || {});
      }),
    );

    this.existingAssociations$ = this.associationService.getCachedContactAssociationsForCompany$(
      this.data.company_id || '',
    );

    this.hasMore$ = combineLatest([this.existingAssociations$, this.visibleContactsCount$$]).pipe(
      map(([existing, visibleContactsCount]) => existing.length > visibleContactsCount),
    );

    const latestContacts$ = combineLatest([
      this.config.routePrefix$,
      this.loadAssociations$$,
      this.existingAssociations$,
      this.changedAssociations$$,
      this.visibleContactsCount$$,
    ]).pipe(
      map(([routePrefix, copyCrmObject, existing, changed, visibleContactsCount]) => {
        const associations = existing.slice(0, visibleContactsCount);
        const associatedObjects = copyCrmObject.map((association) => {
          return {
            associationId: association.associationId,
            associationType: 'Contact-Company',
            tags: [],
            associatedObject: association,
          } as Association;
        });

        const combinedData = [...associations, ...associatedObjects];
        const existingAssociationsCombined = [...existing, ...associatedObjects];
        this.preSelectedRows$$.next(existingAssociationsCombined);
        const dataWithChanges = combineAssociationsWithChanges(combinedData, changed);

        return dataWithChanges
          .filter((association, index, array) => {
            return (
              !!association.associatedObject &&
              array.findIndex((a) => a.associationId === association.associationId) === index
            );
          })
          .map((association) => {
            if (!association.associatedObject) {
              return {} as CompanyToContactAssociationsCardData;
            }
            const fullName = this.contactCustomizationService.getRowFullName(association.associatedObject || {});

            const phoneParsed = parsePhoneNumber(
              this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardExternalIds.PhoneNumber,
              )?.stringValue || '',
            );

            const data = {
              associationId: association.associationId,
              companyName: this.data.name || this.translateService.instant('COMPANY.DEFAULT_NAME'),
              type: 'user',
              userName: fullName,
              tags: (association.tags || []).filter((tag) => tag !== 'Primary'),
              phone: formatAsReadonlyPhone(phoneParsed),
              email: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardExternalIds.Email,
              )?.stringValue,
              jobTitle: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardIds.ContactJobTitle,
              )?.stringValue,
              linkedInUrl: this.fieldService.getFieldValueFromCrmObject(
                association.associatedObject,
                StandardIds.ContactLinkedInURL,
              )?.stringValue,
              id: association.associatedObject.crmObjectId || '',
              profileButtonUrl:
                `${routePrefix}/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`.replace(
                  ':crmObjectId',
                  association.associatedObject.crmObjectId || '',
                ),
            } as CompanyToContactAssociationsCardData;
            return data;
          });
      }),
    );

    this.contacts$ = combineLatest([latestContacts$, this.removedAssociationIds$$.asObservable()]).pipe(
      map(([contacts, removed]) => {
        return contacts.filter((contact) => {
          return !removed.includes(contact.associationId);
        });
      }),
    );

    this.modalData$ = combineLatest([this.fullName$, this.preSelectedRows$$, this.newContact$$]).pipe(
      map(([objectIdAndName, associations, newContact]) => ({
        modalTitle: objectIdAndName,
        primarySelect: '',
        description: '',
        selectedRows: [],
        selectedCompanyNames: [],
        fromId: '',
        copyCrmObjects: [],
        associationType: '',
        associations: associations,
        newSelection: newContact,
      })),
    );
  }

  ngOnInit() {
    this.openCreateContactModal$
      .pipe(
        switchMap((shouldOpen) => {
          if (shouldOpen) {
            return this.dialog
              .open(CrmCreateModalContactComponent, {
                width: '500px',
              })
              .afterClosed();
          }
          return of(null);
        }),
      )
      .subscribe((contact) => {
        if (contact) {
          this.newContact$$.next(contact.crmObjectId);
        }
        this.openModal();
      });
  }

  openModal(): void {
    if (this.canOpenModal) {
      return;
    }
    this.canOpenModal = true;
    this.modalData$
      .pipe(
        take(1),
        tap(() => {
          this.canOpenModal = false;
        }),
        switchMap((data) => {
          const dialogRef = this.dialog.open(SelectObjectTypeModalComponent, {
            data: {
              baseColumnIds: [],
              objectType: 'Contact',
              modalTitle: this.translateService.instant('ASSOCIATIONS.MODAL.TITLE_COMPANY_MODAL', {
                companyName: data.modalTitle,
              }),
              associations: data.associations,
              associationType: 'Contact-Company',
              newSelection: data.newSelection,
              hideCreateButton: true,
            } as ListObjectTypeDialogData,
            injector: Injector.create({
              providers: [
                {
                  provide: PageAnalyticsInjectionToken,
                  useFactory: pageAnalyticsInjectionTokenGenerator('Contact'),
                },
                {
                  provide: GalaxyFilterChipInjectionToken,
                  useFactory: tableFiltersInjectionTokenGenerator('Contact'),
                },
              ],
              parent: this.injector,
            }),
          });
          return dialogRef.afterClosed();
        }),
        tap((modalResponse) => {
          if (modalResponse?.clickedCreate === true) {
            this.openCreateContactModal$$.next(true);
          }
        }),
        map((modalResponse) => {
          let selectedRows = modalResponse?.selectedRows || [];
          if (modalResponse?.clickedCreate === true) {
            selectedRows = new Array<Row>();
          }
          return selectedRows;
        }),
        filter((selectedRows) => !!selectedRows),
        switchMap((selectedRows: Row[]) => {
          const selectedContactsIDs = selectedRows.map((row) => {
            return row.id;
          });
          return this.objectService.getMultiObject('Contact', selectedContactsIDs).pipe(
            map((resp) => {
              const copyCrmObjects = resp.crmObjects?.map((object) => object);
              return {
                copyCrmObjects,
                objects: resp.crmObjects?.map((object) => {
                  return {
                    id: object?.crmObjectId,
                    name: this.contactCustomizationService.getRowFullName(object || {}),
                  };
                }),
              };
            }),
          );
        }),
        tap((selectedObjects) => {
          const updatedObjects = {
            crmObjects: selectedObjects.copyCrmObjects || [],
            objects: selectedObjects.objects || [],
          };
          this.associationService.setCopyCrmObjects(updatedObjects);
        }),
        mergeMap((): Observable<any> => {
          return this.crmAssociationService.createAssociation$('Contact-Company', ['Primary'], true);
        }),
      )

      .subscribe((copyCrmObjects) => {
        if (copyCrmObjects.respCount.error) {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.PARTIAL_SUCCESS', {
            duration: SNACKBAR_DURATION,
            interpolateTranslateParams: {
              createdCount: copyCrmObjects.respCount.createdCount,
              totalCount: copyCrmObjects.respCount.totalCount,
            },
          });
        } else {
          this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.GENERIC', {
            duration: SNACKBAR_DURATION,
          });
        }
        this.loadAssociations$$.next(copyCrmObjects.copyCrmObjectsUpdated);
      });
  }

  removeAssociation(cardData: CompanyToContactAssociationsCardData) {
    return this.dialog
      .open(RemoveAssociationDialogComponent, {
        width: '660px',
        data: {
          associationID: cardData.associationId,
          fromName: cardData.companyName,
          removeName: cardData.userName,
        } as RemoveAssociationData,
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((success) => {
          if (success) {
            this.associationService.removeAssociation(cardData.associationId);
            this.removedAssociationIds$$.next([...this.removedAssociationIds$$.value, cardData.associationId]);
            this.preSelectedRows$$.next(
              this.preSelectedRows$$.getValue().filter((row) => row.associationId !== cardData.associationId),
            );
          }
        }),
      )
      .subscribe();
  }

  editTags(cardData: CompanyToContactAssociationsCardData) {
    return this.defaultAssociationActionsService
      .editTagsAction(cardData)
      .pipe(
        take(1),
        tap((edited) => {
          if (edited === null) {
            return;
          }
          this.changedAssociations$$.next([edited]);
        }),
      )
      .subscribe();
  }

  campaignAction = [
    {
      label: this.translateService.instant('ASSOCIATIONS.CAMPAIGNS.ADD_TO_CAMPAIGN'),
      callback: (data: CardData) => {
        this.openCampaignDialog(data.id || '');
      },
    } as CardAction,
  ];

  openCampaignDialog(id: string): void {
    this.dialog.open(AddCampaignDialogComponent, {
      width: '500px',
      data: {
        id: id,
      },
    });
  }

  showMore(): void {
    this.visibleContactsCount$$.next(this.visibleContactsCount$$.value + this.pageSize);
  }
}
